import React, { useEffect, useRef, useState } from 'react';
import { makeCSS } from '../../utils/makeCSS';
import { SizeVariants } from '../../theme/themes';
import { cx } from '@emotion/css';
import AuthCode, { AuthCodeRef } from 'react-auth-code-input';

/**
 * AuthenticationField is a reusable input component for authentication code.
 * It allows users to enter an authentication code, used for 2FA.
 */
export interface AuthenticationFieldProps {
  /**The size of the fields*/
  size?: SizeVariants.sm | SizeVariants.md | SizeVariants.lg;
  disabled?: boolean;
  onChange?(code: string): void; //return code to parent
  /**Type of allowed characters for your code.*/
  allowedCharacters?: 'alpha' | 'alphanumeric' | 'numeric'; //might be needed for future use
  criticalState?: boolean;
  /** Will clear the input if true */
  shouldClear?: boolean;
  autoFocus?: boolean;
}

const useCSS = makeCSS(({ theme }) => {
  return {
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignContent: 'flex-start',
      alignItems: 'center',
      gap: 8,
      width: theme.spacing[80]
    },

    /**Sizes */
    'size-sm': {
      height: theme.spacing[8],
      fontSize: theme.fontSize[0]
    },
    'size-md': {
      height: theme.spacing[10],
      fontSize: theme.fontSize[1]
    },
    'size-lg': {
      height: theme.spacing[12],
      fontSize: theme.fontSize[2]
    },
    codeField: {
      fontVariantNumeric: 'lining-nums tabular-nums slashed-zero',
      fontFeatureSettings: "'ss02' on",
      textRendering: 'optimizeLegibility',
      fontFamily: theme.fontFamilies.body,
      fontStyle: 'normal',
      fontWeight: theme.fontWeights.regular,
      display: 'flex',
      alignSelf: 'stretch',
      alignItems: 'center',
      textAlign: 'center',
      background: theme.color.underlay['01'],
      border: `1px solid ${theme.color.line['02']}`,
      borderRadius: theme.borderRadius.lg,
      outline: 'none',
      // paddingRight: 12,
      // paddingLeft: 12,
      minWidth: theme.spacing[10],
      width: 46,
      color: theme.color.text['01'],
      '&::placeholder': {
        color: theme.color.text['04']
      },
      '&:focus-within': {
        outline: 'none',
        border: `1px solid ${theme.color.foreground.primary}`,
        boxShadow: '0px 0px 0px 4px rgba(255, 212, 35, 0.12)',
        input: { color: theme.color.text['01'] },
        textarea: { color: theme.color.text['01'] }
      },
      '&.disabled': {
        cursor: 'default',
        opacity: '40%',
        pointerEvents: 'none',
        '&:focus, &:focus-within': {
          border: `1px solid ${theme.color.line['02']}`,
          input: { color: theme.color.text['03'] },
          boxShadow: 'none',
          outline: 'none',
          color: theme.color.text['03']
        }
      }
    },
    criticalState: {
      border: `1px solid ${theme.color.background.critical}`,
      boxShadow: '0px 0px 0px 4px rgba(218, 49, 67, 0.12)',
      '&:focus-within': {
        border: `1px solid ${theme.color.background.critical}`,
        boxShadow: '0px 0px 0px 4px rgba(218, 49, 67, 0.12)'
      }
    }
  };
});

export const AuthenticationField = (props: AuthenticationFieldProps) => {
  const {
    size = SizeVariants.md,
    disabled,
    onChange,
    allowedCharacters = 'numeric',
    criticalState = false,
    shouldClear,
    autoFocus = false
  } = props;

  const authInputRef = useRef<AuthCodeRef>(null);

  const classes = useCSS();

  const [code, setCode] = useState<string | undefined>();
  //updated the code entered by the user
  const handleOnChange = (res: string) => {
    setCode(res);
    onChange(res);
  };

  useEffect(() => {
    if (shouldClear && authInputRef.current) {
      authInputRef.current.clear();
    }
  }, [shouldClear]);

  return (
    <div>
      <AuthCode
        allowedCharacters={allowedCharacters}
        autoFocus={autoFocus}
        containerClassName={classes.root}
        inputClassName={cx(
          classes.codeField,
          classes[`size-${size}`],
          criticalState ? classes.criticalState : ''
        )}
        onChange={handleOnChange}
        disabled={disabled}
        placeholder="0"
        ref={authInputRef}
      />
    </div>
  );
};
